import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { put, takeLatest, call, all, takeEvery, select, take } from 'redux-saga/effects';
import isEmpty from 'lodash/isEmpty';
import ddt from '@wix/da-ddt';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { actions as userActions } from '../../redux/users';
import { requestNapi } from '@wix/da-http-client';
import { getIsLoggedIn } from '../../publicSession/selectors';
import { restrictLoggedOut } from '../../redux/loggedout';
import { addNotification, removeNotification } from '../../Notifications/redux/actionCreators';
import { NotificationType } from '../../Notifications';
import { awardBadgeSuccess } from '../../coreBadge/redux/actionCreators';
import { ResourceType } from '../../types/resource';
import { requestSuccess, requestFailure, reportComment, likeComment, featureComment, unfeatureComment, unlikeComment, hideComment, unhideComment, reduxModuleLoaded, updateCommentBadges } from './actionCreators';
import { RequestError } from './types';
import { getCommentsByUser, getCommentState } from './selectors';
import { putErrorNotification } from '../../utils/saga';
import i18next from 'i18next';
function fakeResponse(options) {
    ddt.log('comments', 'faking the response!');
    if (process.env.NODE_ENV !== 'production') {
        if (typeof window !== 'undefined' && window.doError) {
            return {
                error: 'invalid_request',
                errorDescription: 'Not logged in.',
                status: 'error'
            };
        }
        // eslint-disable-next-line
        const { comments } = require('@wix/da-sample-data');
        return {
            success: true,
            comment: comments[0]
        };
    }
}
function getError(result) {
    return result.errorDescription;
}
function* doActionRequest(action, type, comment, data) {
    const isLoggedIn = yield select(getIsLoggedIn);
    if (!isLoggedIn) {
        yield put(restrictLoggedOut({
            restrictedActionName: ReferralType.COMMENT_ACTION
        }));
        return;
    }
    ddt.log('comments', `calling ${action} comment saga`);
    const result = yield call(requestNapi, {
        method: 'post',
        url: `/comments${action}`,
        data: _object_spread_props(_object_spread({}, data || {
            itemid: comment.itemId,
            commentid: comment.commentId
        }), {
            editor_v3: 'yes'
        })
    }, fakeResponse);
    if (result && result.success) {
        yield put(requestSuccess(type, comment));
        return true;
    } else {
        let errorAction = 'update';
        switch(type){
            case featureComment.toString():
                errorAction = 'feature';
                break;
            case unfeatureComment.toString():
                errorAction = 'unfeature';
                break;
            case likeComment.toString():
                errorAction = 'like';
                break;
            case unlikeComment.toString():
                errorAction = 'unlike';
                break;
            case hideComment.toString():
                errorAction = 'delete';
                break;
            case unhideComment.toString():
                errorAction = 'restore';
                break;
            case reportComment.toString():
                errorAction = 'report';
                break;
            case userActions.blockUserFail.toString():
                errorAction = 'block user';
                break;
            default:
        }
        yield putErrorNotification(i18next.t('comment.error.action', {
            action: errorAction,
            error: getError(result)
        }));
        yield put(requestFailure(type, comment, getError(result)));
        return false;
    }
}
function* featureCommentSaga(action) {
    if (!action.payload) {
        return;
    }
    const { type, payload: { comment } } = action;
    yield call(doActionRequest, '/feature', type, comment);
}
function* unfeatureCommentSaga(action) {
    if (!action.payload) {
        return;
    }
    const { type, payload: { comment } } = action;
    yield call(doActionRequest, '/unfeature', type, comment);
}
function* likeCommentSaga(action) {
    if (!action.payload) {
        return;
    }
    const { type, payload: { comment } } = action;
    yield call(doActionRequest, '/like', type, comment);
}
function* unlikeCommentSaga(action) {
    if (!action.payload) {
        return;
    }
    const { type, payload: { comment } } = action;
    yield call(doActionRequest, '/unlike', type, comment);
}
function* reportCommentSaga(action) {
    if (!action.payload) {
        return;
    }
    const { type, payload: { comment } } = action;
    yield call(doActionRequest, '/report', type, comment);
}
function* hideCommentSaga(action) {
    if (!action.payload) {
        return;
    }
    const { type, payload: { comment, insideModal } } = action;
    const done = yield call(doActionRequest, '/hide', type, comment);
    if (done) {
        yield put(addNotification(NotificationType.DeleteComment, {
            id: comment.commentId,
            comment,
            insideModal
        }));
    }
}
function* unhideCommentSaga(action) {
    if (!action.payload) {
        return;
    }
    const { type, payload: { comment } } = action;
    const done = yield call(doActionRequest, '/unhide', type, comment);
    if (done) {
        yield put(removeNotification(comment.commentId));
    }
}
function* blockUserSuccessSaga(action) {
    if (!action.payload) {
        return;
    }
    const { type, payload: blockedUserId } = action;
    const commentsByUser = yield select(getCommentsByUser, blockedUserId);
    yield all(commentsByUser.map((comment)=>put(requestSuccess(type, comment))));
}
function* blockUserFailSaga(action) {
    if (!action.payload) {
        return;
    }
    const { type, payload: { user, result } } = action;
    const error = getError(result) || RequestError.GeneralError;
    const commentsByUser = yield select(getCommentsByUser, user.userId);
    yield all(commentsByUser.map((comment)=>put(requestFailure(type, comment, error))));
}
function* handleAwardBadge(action) {
    const { recipient, allBadges } = action.payload;
    if (!(recipient.typeId === ResourceType.COMMENT)) {
        return;
    }
    const overridenCommentState = yield select(getCommentState(recipient.itemId));
    yield put(updateCommentBadges(!isEmpty(overridenCommentState) ? overridenCommentState : recipient.comment, allBadges));
}
export default function* saga() {
    yield take(reduxModuleLoaded);
    yield all([
        takeLatest(featureComment, featureCommentSaga),
        takeLatest(unfeatureComment, unfeatureCommentSaga),
        takeLatest(likeComment, likeCommentSaga),
        takeLatest(unlikeComment, unlikeCommentSaga),
        takeLatest(hideComment, hideCommentSaga),
        takeLatest(unhideComment, unhideCommentSaga),
        takeLatest(reportComment, reportCommentSaga),
        takeEvery(userActions.blockUserSuccess, blockUserSuccessSaga),
        takeEvery(userActions.blockUserFail, blockUserFailSaga),
        takeEvery(awardBadgeSuccess, handleAwardBadge)
    ]);
}

//# sourceMappingURL=saga.js.map